import { Query, useQuery } from "@tanstack/react-query";

import { apiClient } from "@/api/apiClient";
import {
  getTaskResultApiV1BundleSplitterTasksTaskIdResultGet,
  getTaskStatusApiV1BundleSplitterTasksTaskIdStatusGet,
  healthCheckApiV1BundleSplitterHealthGet,
  listBundleSplitterTasksApiV1BundleSplitterTasksGet,
} from "@/api/generated/sdk.gen";
import { BundleSplitterTaskStatusEnum } from "@/api/generated/types.gen";

import { CLIENT_KEYS } from "@constants/clientKeys";

/**
 * Check the health status of the bundle splitter API
 * @param enabled - Whether the query should run
 * @returns Query result with health status data
 */
export const useBundleSplitterHealth = (enabled = true) => {
  return useQuery({
    queryKey: [CLIENT_KEYS.BUNDLE_SPLITTER, CLIENT_KEYS.HEALTH],
    queryFn: async () => {
      const response = await healthCheckApiV1BundleSplitterHealthGet({
        client: apiClient,
      });

      // Check for errors in the response
      if ("error" in response && response.error) {
        const errorDetail =
          typeof response.error === "object" &&
          response.error !== null &&
          "detail" in response.error
            ? response.error.detail
            : JSON.stringify(response.error);
        throw new Error(`Failed to check bundle splitter health: ${errorDetail}`);
      }

      return response.data;
    },
    enabled,
  });
};

/**
 * Get the status of a bundle splitter classification task
 * @param task_id - The task ID to check status for
 * @param enabled - Whether the query should run
 * @param refetchInterval - Optional interval for automatic refetching (useful for polling)
 * @returns Query result with task status data
 */
export const useTaskStatus = ({
  task_id,
  enabled = true,
  refetchInterval = 0,
}: {
  task_id: string;
  enabled?: boolean;
  refetchInterval?:
    | number
    | false
    | ((
        query: Query<unknown, Error, unknown, readonly unknown[]>
      ) => number | false | undefined);
}) => {
  return useQuery({
    queryKey: [CLIENT_KEYS.BUNDLE_SPLITTER, CLIENT_KEYS.STATUS, task_id],
    queryFn: async () => {
      const response = await getTaskStatusApiV1BundleSplitterTasksTaskIdStatusGet({
        client: apiClient,
        path: {
          task_id,
        },
      });

      // Check for errors in the response
      if ("error" in response && response.error) {
        const errorDetail =
          typeof response.error === "object" &&
          response.error !== null &&
          "detail" in response.error
            ? response.error.detail
            : JSON.stringify(response.error);
        throw new Error(`Failed to get task status: ${errorDetail}`);
      }

      return response.data;
    },
    enabled: enabled && !!task_id,
    refetchInterval,
  });
};

/**
 * Get the result of a bundle splitter classification task
 * @param task_id - The task ID to get results for
 * @param enabled - Whether the query should run
 * @returns Query result with task result data
 */
export const useTaskResult = ({
  task_id,
  enabled = true,
}: {
  task_id: string;
  enabled?: boolean;
}) => {
  return useQuery({
    queryKey: [CLIENT_KEYS.BUNDLE_SPLITTER, CLIENT_KEYS.RESULT, task_id],
    queryFn: async () => {
      const response = await getTaskResultApiV1BundleSplitterTasksTaskIdResultGet({
        client: apiClient,
        path: {
          task_id,
        },
      });

      // Check for errors in the response
      if ("error" in response && response.error) {
        const errorDetail =
          typeof response.error === "object" &&
          response.error !== null &&
          "detail" in response.error
            ? response.error.detail
            : JSON.stringify(response.error);
        throw new Error(`Failed to get task result: ${errorDetail}`);
      }

      return response.data;
    },
    enabled: enabled && !!task_id,
  });
};

/**
 * List bundle splitter tasks with optional filtering
 * @param case_number - Optional case number to filter tasks by
 * @param status - Optional status to filter tasks by (e.g., 'PENDING', 'PROCESSING', 'SUCCESS', 'FAILURE', 'REVOKED')
 * @param enabled - Whether the query should run
 * @param refetchInterval - Optional interval for automatic refetching in milliseconds (useful for polling)
 * @returns Query result with list of bundle splitter tasks
 */
export const useBundleSplitterTasks = ({
  case_number,
  status,
  enabled = true,
  refetchInterval,
}: {
  case_number?: string;
  status?: BundleSplitterTaskStatusEnum | null;
  enabled?: boolean;
  refetchInterval?: number | false;
} = {}) => {
  return useQuery({
    queryKey: [
      CLIENT_KEYS.BUNDLE_SPLITTER,
      CLIENT_KEYS.TASKS,
      { case_number, status },
    ],
    queryFn: async () => {
      // Build the query parameters, only including those that are defined
      const queryParams: Record<string, string> = {};
      if (case_number) queryParams.case_number = case_number;
      if (status) queryParams.status = status;

      const response = await listBundleSplitterTasksApiV1BundleSplitterTasksGet({
        client: apiClient,
        query: queryParams,
      });

      // Check for errors in the response
      if ("error" in response && response.error) {
        const errorDetail =
          typeof response.error === "object" &&
          response.error !== null &&
          "detail" in response.error
            ? response.error.detail
            : JSON.stringify(response.error);
        throw new Error(`Failed to list bundle splitter tasks: ${errorDetail}`);
      }

      return response.data;
    },
    enabled,
    refetchInterval,
  });
};
